import { Children, cloneElement, isValidElement, ReactNode } from 'react';

/**
 * Dynamically wraps the children intended for a Radix UI `Slot` with a specified wrapper element or component.
 * This utility function is particularly useful when working with Radix UI's slotting mechanism, enabling the conditional
 * application of additional styling or structure around slotted children based on the `asChild` prop.
 *
 * The function checks the `asChild` prop: if it's true, indicating that the children are being directly passed
 * to a `Slot` component, it attempts to wrap only the slottable children with the provided wrapper. This is required
 * for cases where the default rendering needs to be overridden or augmented for specific design requirements.
 *
 * ```tsx
 * // Usage in a component to conditionally wrap its children with additional styling:
 *
 * const MyComponent = ({asChild, children}) => {
 *   const Comp = asChild ? Slot : 'button';
 *
 *   return (
 *     <Comp>
 *       <Icon />
 *       <Slottable>
 *         {wrapSlottableChildren(asChild, children, (children) => (
 *           <span className="underline">{children}</span>
 *         ))}
 *       </Slottable>
 *     </Comp>
 *   );
 * };
 *
 * // Example usage:
 * <MyComponent asChild>
 *   <a href="#">Click Me!</a>
 * </MyComponent>
 *
 * // This results in the following rendering when `asChild` is true:
 * // <a href="#">
 * //   <div className="icon" ... />
 * //   <span className="underline">Click Me!</span>
 * // </a>
 * ```
 */
export const wrapSlottableChildren = (
  asChild: boolean | undefined,
  children: ReactNode,
  wrapper: (children: ReactNode) => ReactNode,
) => {
  if (!children) {
    return null;
  }

  if (!asChild) {
    return wrapper(children);
  }

  const childrenAsArray = Children.toArray(children);

  if (childrenAsArray.length !== 1) {
    return wrapper(children);
  }

  const child = childrenAsArray[0];

  if (!isValidElement<{ children?: ReactNode }>(child)) {
    return wrapper(children);
  }

  return cloneElement(child, undefined, wrapper(child.props.children));
};
